<template>
  <div class="sleek-player-wrapper">
      <video ref="videoPlayer"
      class="video-js vjs-big-play-centered vjs-16-9 sleek-video-js"
      :playsinline="true"
      @contextmenu.prevent
      controlsList="nodownload"></video>
  </div>
</template>

<script>
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
// Removed the bulky 'sea' theme to use our own sleek custom styling

export default {
  name: "VideoPlayer",
  props: {
    options: { type: Object, required: true },
    previewMode: { type: Boolean, default: false }
  },

  data() {
      return {
          player: null,
          currentTime: 0,
          retryTimeout: null
      }
  },

  methods: {
      onSeeking() {
        if (!this.player) return
        if (this.$store.state.auth.isSuperAdmin || this.previewMode) return

        if (this.player.currentTime() > this.currentTime) {
          // Prevent forwarding
          this.player.currentTime(this.currentTime - 0.01)
        }
      },

      onVideoPlayerTimeUpdate() {
        if (!this.player) return
        if (this.player.seeking()) return

        this.currentTime = this.player.currentTime()
      },
  },

  mounted() {
      this.player = videojs(this.$refs.videoPlayer, this.options, () => {
        // load on error
        let retries = 4;
        this.player.on('error', () => {
          if (retries--){
              // Save it to the variable!
              this.retryTimeout = setTimeout(() => {
                  if (this.player && !this.player.isDisposed()) {
                    this.player.pause();
                    this.player.load();
                  }
              }, 4000);
          }
          if (retries < 0) this.$toast.error('The video failed to load due to a poor network connection.')
        })

        this.player.on('ended', () => {
          this.$emit('playerEnded', true)
        })

        this.player.on('seeking', () => {
          this.onSeeking()
        })

        this.player.on('timeupdate', () => {
          this.onVideoPlayerTimeUpdate()
        })
      })
  },

  beforeUnmount() {
    clearTimeout(this.retryTimeout); // Kill the ghost timer!
    if (this.player && !this.player.isDisposed()) {
      this.player.dispose();
    }
  }
}
</script>

<style>
/* Wrapper to give it the rounded "Theater" look */
.sleek-player-wrapper {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  background-color: #000;
}

.sleek-video-js {
  font-family: 'Roboto', sans-serif !important;
  border-radius: 12px;
}

/* Force the actual HTML5 video and poster to respect the curves */
.sleek-video-js .vjs-tech,
.sleek-video-js .vjs-poster {
  /* border-radius: 12px; */
}

/* --- Fix the floating 1x speed text --- */
.sleek-video-js .vjs-playback-rate .vjs-playback-rate-value {
  line-height: 50px; /* Aligns the text vertically with the other 50px icons */
  font-size: 1.2em;
  font-weight: 600;
}

/* Big Center Play Button */
.sleek-video-js .vjs-big-play-button {
  background-color: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  width: 80px;
  height: 80px;
  line-height: 80px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.2);
  transition: all 0.2s ease;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
}

.sleek-video-js:hover .vjs-big-play-button {
  background-color: #00a0c1;
  border-color: #00a0c1;
  transform: translate(-50%, -50%) scale(1.1);
}

.sleek-video-js .vjs-big-play-button .vjs-icon-placeholder:before {
  font-size: 40px;
  line-height: 76px;
}

/* Control Bar - Gradient Background */
.sleek-video-js .vjs-control-bar {
  background: linear-gradient(to top, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0) 100%);
  height: 50px;
  display: flex;
  align-items: center;
}

/* Progress Bar (Scrubber) */
.sleek-video-js .vjs-progress-control {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.sleek-video-js .vjs-slider {
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

/* The played portion (Teal Brand Color) */
.sleek-video-js .vjs-play-progress {
  background-color: #00a0c1;
  border-radius: 4px;
}

/* Hide the dot until hover */
.sleek-video-js .vjs-play-progress:before {
  font-size: 14px;
  color: #00a0c1;
  top: -0.35em;
  text-shadow: 0 0 4px rgba(0,0,0,0.5);
  opacity: 0;
  transform: scale(0.5);
  transition: all 0.2s ease;
}

/* Hover effect: Bar gets thicker, dot appears */
.sleek-video-js .vjs-progress-holder {
  height: 4px;
  transition: all 0.2s ease;
}
.sleek-video-js .vjs-progress-control:hover .vjs-progress-holder {
  height: 8px;
}
.sleek-video-js .vjs-progress-control:hover .vjs-play-progress:before {
  opacity: 1;
  transform: scale(1);
}

/* Buttons and Spacing */
.sleek-video-js .vjs-button > .vjs-icon-placeholder:before {
  font-size: 1.6em;
  line-height: 50px;
}

.sleek-video-js .vjs-time-control {
  line-height: 50px;
  font-size: 0.95em;
  font-weight: 500;
  opacity: 0.8;
}

.vjs-menu li {
  font-size: 12px !important;
  text-transform: capitalize !important;
}
</style>